import React from "react";
import AdminNavbar from "../Navbars/AdminNavbar.js";
import PortalSidebar from "./PortalSidebar";
import { PortalApis } from "../../apis/PortalApis";
import { Oval } from 'react-loader-spinner';
import { FaFileInvoiceDollar, FaDownload, FaEye } from "react-icons/fa";

export default function PortalInvoices() {

    const [invoices, setInvoices] = React.useState<any[]>([]);
    const [meta, setMeta] = React.useState<any>(null);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [statusFilter, setStatusFilter] = React.useState<string>('');

    // Detail view
    const [selectedInvoice, setSelectedInvoice] = React.useState<any>(null);
    const [detailLoading, setDetailLoading] = React.useState<boolean>(false);

    React.useEffect(() => { loadInvoices(); }, [statusFilter]);

    const loadInvoices = () => {
        setLoading(true);
        PortalApis.getInvoices(statusFilter).then((response: any) => {
            setLoading(false);
            if (response?.data?.data) {
                setInvoices(response.data.data);
                setMeta(response.data.meta || null);
            }
        }).catch(() => setLoading(false));
    };

    const viewInvoice = (id: any) => {
        setDetailLoading(true);
        PortalApis.getInvoice(id).then((response: any) => {
            setDetailLoading(false);
            if (response?.data?.data) {
                setSelectedInvoice(response.data.data);
            }
        }).catch(() => setDetailLoading(false));
    };

    const downloadInvoice = (id: any) => {
        PortalApis.downloadInvoice(id).then((response: any) => {
            if (response?.data?.data) {
                // Open print-friendly version or trigger PDF generation
                const invoiceData = response.data.data;
                const printWindow = window.open('', '_blank');
                if (printWindow) {
                    printWindow.document.write(generateInvoiceHtml(invoiceData));
                    printWindow.document.close();
                    printWindow.print();
                }
            }
        });
    };

    const getStatusBadge = (status: string) => {
        const styles: any = {
            draft: 'bg-gray-100 text-gray-600',
            sent: 'bg-blue-100 text-blue-700',
            viewed: 'bg-purple-100 text-purple-700',
            paid: 'bg-green-100 text-green-700',
            overdue: 'bg-red-100 text-red-700',
            cancelled: 'bg-gray-100 text-gray-500',
        };
        return (
            <span className={`px-2 py-1 rounded-full text-[11px] font-semibold ${styles[status] || 'bg-gray-100 text-gray-600'}`}>
                {status?.toUpperCase()}
            </span>
        );
    };

    const generateInvoiceHtml = (data: any) => {
        const itemRows = (data.items || []).map((item: any, i: number) => `
      <tr>
        <td style="padding:8px;border-bottom:1px solid #eee">${i + 1}</td>
        <td style="padding:8px;border-bottom:1px solid #eee">${item.shipment_number || '-'}</td>
        <td style="padding:8px;border-bottom:1px solid #eee">${item.date || '-'}</td>
        <td style="padding:8px;border-bottom:1px solid #eee">${item.origin || '-'}</td>
        <td style="padding:8px;border-bottom:1px solid #eee">${item.destination || '-'}</td>
        <td style="padding:8px;border-bottom:1px solid #eee">${item.weight}</td>
        <td style="padding:8px;border-bottom:1px solid #eee;text-align:right">₦${Number(item.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
      </tr>
    `).join('');

        return `
      <!DOCTYPE html>
      <html><head><title>Invoice ${data.invoice_number}</title>
      <style>body{font-family:Arial,sans-serif;margin:40px;color:#333}table{width:100%;border-collapse:collapse}th{background:#f5f5f5;text-align:left;padding:8px;border-bottom:2px solid #ddd}td{padding:8px}</style>
      </head><body>
        <div style="display:flex;justify-content:space-between;margin-bottom:30px">
          <div><h1 style="color:#E04343;margin:0">INVOICE</h1><p style="color:#999">${data.invoice_number}</p></div>
          <div style="text-align:right"><h3 style="margin:0">${data.bill_from?.company || 'Skynet Logistics'}</h3><p style="color:#999">${data.bill_from?.email || ''}</p></div>
        </div>
        <div style="display:flex;justify-content:space-between;margin-bottom:30px">
          <div><p style="color:#999;margin:0">Bill To:</p><h4 style="margin:4px 0">${data.bill_to?.company || ''}</h4><p style="margin:0">${data.bill_to?.email || ''}</p></div>
          <div style="text-align:right"><p style="margin:2px 0"><strong>Date:</strong> ${data.date_issued}</p><p style="margin:2px 0"><strong>Due:</strong> ${data.due_date}</p><p style="margin:2px 0"><strong>Period:</strong> ${data.period}</p><p style="margin:2px 0"><strong>Status:</strong> ${data.status?.toUpperCase()}</p></div>
        </div>
        <table><thead><tr><th>#</th><th>AWB</th><th>Date</th><th>From</th><th>To</th><th>Weight</th><th style="text-align:right">Amount</th></tr></thead><tbody>${itemRows}</tbody></table>
        <div style="margin-top:20px;text-align:right">
          <p>Subtotal: <strong>₦${Number(data.subtotal || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong></p>
          <p>VAT (${data.tax_rate}): <strong>₦${Number(data.tax_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong></p>
          <h2 style="color:#E04343">Total: ₦${Number(data.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</h2>
        </div>
      </body></html>
    `;
    };

    // ── Detail View ──
    if (selectedInvoice) {
        const inv = selectedInvoice.invoice || selectedInvoice;
        const summary = selectedInvoice.summary || {};
        return (
            <>
                <PortalSidebar title="Invoice Detail" />
                <div className="relative md:ml-64 bg-[#FAFAFA]">
                    <AdminNavbar title="Invoice Detail" />
                    <div className="lg:px-10 pb-6 px-6 bg-[#FAFAFA] min-h-screen">

                        {/* Back button */}
                        <div className="lg:pt-5 pt-3 mb-4">
                            <button onClick={() => setSelectedInvoice(null)}
                                className="flex items-center gap-2 text-[#E04343] text-[14px] hover:underline">
                                ← Back to Invoices
                            </button>
                        </div>

                        {/* Invoice Header */}
                        <div className="bg-white rounded-[10px] border border-[#E2E8F0] px-[26px] py-[24px] mb-6">
                            <div className="flex justify-between items-start flex-wrap gap-4">
                                <div>
                                    <h2 className="text-[24px] font-[700] text-[#263238]">{inv.invoice_number}</h2>
                                    <p className="text-[13px] text-[#999999] mt-1">
                                        {summary.period || `${inv.period_start} – ${inv.period_end}`}
                                    </p>
                                </div>
                                <div className="flex items-center gap-3">
                                    {getStatusBadge(inv.status)}
                                    <button onClick={() => downloadInvoice(inv.id)}
                                        className="flex items-center gap-2 px-4 py-2 bg-[#E04343] text-white rounded-[8px] text-[13px] hover:bg-[#c93636]">
                                        <FaDownload size={12} /> Download
                                    </button>
                                </div>
                            </div>

                            {/* Summary cards */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                                <div className="bg-[#FAFAFA] rounded-[8px] p-4">
                                    <p className="text-[12px] text-[#999]">Shipments</p>
                                    <p className="text-[20px] font-[700]">{summary.total_shipments || inv.total_shipments || 0}</p>
                                </div>
                                <div className="bg-[#FAFAFA] rounded-[8px] p-4">
                                    <p className="text-[12px] text-[#999]">Total Weight</p>
                                    <p className="text-[20px] font-[700]">{summary.total_weight || inv.total_weight || '0 kg'}</p>
                                </div>
                                <div className="bg-[#FAFAFA] rounded-[8px] p-4">
                                    <p className="text-[12px] text-[#999]">Due Date</p>
                                    <p className="text-[20px] font-[700]">{summary.due_date || inv.due_date || '-'}</p>
                                </div>
                                <div className="bg-[#FAFAFA] rounded-[8px] p-4">
                                    <p className="text-[12px] text-[#999]">Total Amount</p>
                                    <p className="text-[20px] font-[700] text-[#E04343]">
                                        ₦{Number(summary.total || inv.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Line Items */}
                        <div className="px-6 bg-white pt-[24px] rounded-[10px] pb-10">
                            <span className="font-[700]">Shipment Details</span>
                            <div className="relative overflow-x-auto mt-6">
                                <table className="w-full text-sm text-left text-gray-500">
                                    <thead className="text-[12px] text-[#808080] bg-[#FAFAFA]">
                                        <tr>
                                            <th className="px-2 py-2">S/N</th>
                                            <th className="px-2 py-2">AWB</th>
                                            <th className="px-2 py-2">Date</th>
                                            <th className="px-2 py-2">From</th>
                                            <th className="px-2 py-2">To</th>
                                            <th className="px-2 py-2">Weight</th>
                                            <th className="px-2 py-2 text-right">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(inv.items || []).map((item: any, i: number) => (
                                            <tr key={item.id || i} className="bg-white border-b text-[13px]">
                                                <td className="px-2 py-3">{i + 1}</td>
                                                <td className="px-2 py-3 font-mono text-[12px]">{item.shipment_number || '-'}</td>
                                                <td className="px-2 py-3">{item.shipment_date || '-'}</td>
                                                <td className="px-2 py-3">{item.origin || '-'}</td>
                                                <td className="px-2 py-3">{item.destination || '-'}</td>
                                                <td className="px-2 py-3">{item.weight} kg</td>
                                                <td className="px-2 py-3 text-right">₦{Number(item.agreed_rate || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Totals */}
                            <div className="flex justify-end mt-6">
                                <div className="w-[250px]">
                                    <div className="flex justify-between py-2 text-[13px]">
                                        <span className="text-[#999]">Subtotal</span>
                                        <span>₦{Number(inv.subtotal || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                    </div>
                                    <div className="flex justify-between py-2 text-[13px]">
                                        <span className="text-[#999]">VAT ({inv.tax_rate || 7.5}%)</span>
                                        <span>₦{Number(inv.tax_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                    </div>
                                    <div className="flex justify-between py-2 text-[16px] font-[700] border-t border-[#E2E8F0]">
                                        <span>Total</span>
                                        <span className="text-[#E04343]">₦{Number(inv.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </>
        );
    }

    // ── List View ──
    return (
        <>
            <PortalSidebar title="Invoices" />
            <div className="relative md:ml-64 bg-[#FAFAFA]">
                <AdminNavbar title="Invoices" />
                <div className='lg:px-10 pb-6 px-6 bg-[#FAFAFA] min-h-screen'>

                    {/* Header */}
                    <div className="lg:pt-5 pt-3">
                        <div className="bg-white rounded-[10px] border border-[#E2E8F0] px-[26px] py-[24px]">
                            <div className="flex items-center gap-3 mb-2">
                                <FaFileInvoiceDollar className="text-[#E04343]" size={24} />
                                <h3 className="text-[20px] font-semibold">Invoices</h3>
                            </div>
                            <p className="text-[13px] text-[#999999]">
                                View and download your billing invoices. Invoices are generated based on your agreed rate.
                            </p>
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="flex gap-2 mt-4">
                        {['', 'sent', 'viewed', 'paid', 'overdue'].map((s) => (
                            <button key={s}
                                onClick={() => setStatusFilter(s)}
                                className={`px-3 py-1.5 rounded-full text-[12px] font-[500] border transition-all ${statusFilter === s
                                    ? 'bg-[#E04343] text-white border-[#E04343]'
                                    : 'bg-white text-[#808080] border-[#E2E8F0] hover:border-[#E04343]'
                                    }`}>
                                {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                            </button>
                        ))}
                    </div>

                    {/* Table */}
                    <div className="px-6 mt-4 bg-white pt-[24px] rounded-[10px] pb-10">
                        <div className="relative overflow-x-auto">
                            {loading ? (
                                <div className="flex justify-center py-16">
                                    <Oval visible={true} height="35" width="35" color="#E04343" secondaryColor="#FCBF94" />
                                </div>
                            ) : invoices.length === 0 ? (
                                <div className="text-center py-16 text-[#999]">
                                    <FaFileInvoiceDollar size={40} className="mx-auto mb-3 text-[#ddd]" />
                                    <p>No invoices yet</p>
                                </div>
                            ) : (
                                <table className="w-full text-sm text-left text-gray-500">
                                    <thead className="text-[12px] text-[#808080] bg-[#FAFAFA]">
                                        <tr>
                                            <th className="px-2 py-2">Invoice #</th>
                                            <th className="px-2 py-2">Period</th>
                                            <th className="px-2 py-2">Shipments</th>
                                            <th className="px-2 py-2">Total</th>
                                            <th className="px-2 py-2">Due Date</th>
                                            <th className="px-2 py-2">Status</th>
                                            <th className="px-2 py-2">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoices.map((inv: any) => (
                                            <tr key={inv.id} className="bg-white border-b text-[13px] hover:bg-[#FAFAFA]">
                                                <td className="px-2 py-3 font-mono text-[12px] font-[600]">{inv.invoice_number}</td>
                                                <td className="px-2 py-3">{inv.period_start} – {inv.period_end}</td>
                                                <td className="px-2 py-3">{inv.total_shipments}</td>
                                                <td className="px-2 py-3 font-[600]">
                                                    ₦{Number(inv.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                                </td>
                                                <td className="px-2 py-3">{inv.due_date}</td>
                                                <td className="px-2 py-3">{getStatusBadge(inv.status)}</td>
                                                <td className="px-2 py-3">
                                                    <div className="flex gap-2">
                                                        <button onClick={() => viewInvoice(inv.id)} className="text-[#E04343] hover:underline text-[12px] flex items-center gap-1">
                                                            <FaEye size={12} /> View
                                                        </button>
                                                        <button onClick={() => downloadInvoice(inv.id)} className="text-[#808080] hover:underline text-[12px] flex items-center gap-1">
                                                            <FaDownload size={12} /> PDF
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>

                </div>
            </div>
        </>
    );
}